import { useCallback, useEffect, useState } from "react";
import { api, FirmAccount, FirmBook, FirmOverview, Manifest, MoneyTree, openView } from "../api";
import { fmtTime } from "../format";
import { pct, signedUsd, usd, usdCompact } from "../lib/money";

// THE FIRM — the landing view, and the only screen that shows the WHOLE.
// Everything else in the app is a slice of one book; this reads top-down:
// what it's all worth → where the money physically sits → what each book is
// doing with it → each book's sleeves. Clicking a book drills into Command on
// that book, which is the whole → part move the rest of the app assumes you've
// already made.
//
// Two rules it inherits from the backend and must not soften:
//   * REAL and PAPER are separate trees, never summed. Real is the default;
//     paper is opt-in, dashed, and labelled as a simulation every time.
//   * A `partial` total is a LOWER BOUND. Say the venue is unreachable — never
//     let a smaller number read as the truth (Kaizen D2/F6).

type MoneyType = "real" | "paper";

export default function Firm({ manifest }: { manifest: Manifest }) {
  const [ov, setOv] = useState<FirmOverview | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [money, setMoney] = useState<MoneyType>("real");
  const [open, setOpen] = useState<Record<string, boolean>>({});

  const load = useCallback(async () => {
    try { setOv(await api.get<FirmOverview>("/api/firm/overview")); setError(null); }
    catch (e) { setError((e as Error).message); }
  }, []);

  // 30s, not the usual 15 — this one read touches every venue.
  useEffect(() => {
    void load();
    const iv = setInterval(load, 30000);
    return () => clearInterval(iv);
  }, [load]);

  const tree: MoneyTree | null = ov ? ov[money] : null;
  const paper = money === "paper";

  return (
    <div className="space-y-4">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <h1 className="text-lg font-bold">The Firm</h1>
        <span className="text-xs text-slate-500">{manifest.tenant}&rsquo;s capital, whole</span>
      </div>

      <div className="flex items-center gap-1.5 flex-wrap">
        <Segment on={!paper} onClick={() => setMoney("real")} tone="real">Real money</Segment>
        <Segment on={paper} onClick={() => setMoney("paper")} tone="paper">Paper</Segment>
        {paper && (
          <span className="text-xs text-gold font-semibold ml-1">
            Simulated — not real money. Never part of what you own.
          </span>
        )}
      </div>

      {error && <div className="card border-down/60 text-down text-sm">{error}</div>}
      {!ov && !error && <div className="text-slate-500 text-sm">reading every venue…</div>}

      {ov && tree && (
        <>
          <Hero tree={tree} asOf={ov.as_of} paper={paper} />

          <section className="space-y-2">
            <h2 className="label mb-0">Where the money is</h2>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {tree.accounts.map((a) => <AccountCard key={a.key} a={a} paper={paper} />)}
              {tree.accounts.length === 0 && (
                <div className="text-slate-600 text-sm">no accounts on this side of the books</div>
              )}
            </div>
          </section>

          <section className="space-y-2">
            <h2 className="label mb-0">Books</h2>
            {tree.books.map((b) => (
              <BookCard key={b.book} b={b} paper={paper}
                        open={!!open[b.book]}
                        onToggle={() => setOpen((o) => ({ ...o, [b.book]: !o[b.book] }))} />
            ))}
            {tree.books.length === 0 && <div className="text-slate-600 text-sm">no books here</div>}
            <Buffers buffers={tree.buffers} />
          </section>

          <div className="text-xs text-slate-600 flex items-center gap-2 flex-wrap pt-1">
            <span>Position-by-position detail lives on</span>
            <button className="underline hover:text-slate-400" onClick={() => openView("holdings")}>Holdings</button>
            <span>· closed-trade results on</span>
            <button className="underline hover:text-slate-400" onClick={() => openView("ledger")}>Performance</button>
          </div>
        </>
      )}
    </div>
  );
}

function Hero({ tree, asOf, paper }: { tree: MoneyTree; asOf: string; paper: boolean }) {
  const down = tree.accounts.filter((a) => a.status !== "ok");
  const tone = tree.day_change == null ? "text-slate-400" : tree.day_change >= 0 ? "text-up" : "text-down";
  return (
    <div className={`card ${paper ? "border-dashed border-slate-700 bg-panel/40" : ""}`}>
      <div className="label">{paper ? "Simulated capital (not real)" : "Capital under management"}</div>
      <div className={`font-mono font-black leading-none text-4xl sm:text-5xl break-words
                       ${paper ? "text-slate-400" : ""}`}>
        {usdCompact(tree.total)}
      </div>
      <div className="mt-2 flex items-baseline gap-x-3 gap-y-1 flex-wrap text-sm">
        <span className={`font-mono font-bold ${tone}`}>
          {tree.day_change == null ? "day change not marked yet" : signedUsd(tree.day_change)}
        </span>
        {tree.day_change != null && tree.day_change_pct != null && (
          <span className={`font-mono ${tone}`}>{pct(tree.day_change_pct)}</span>
        )}
        <span className="text-xs text-slate-500">today</span>
        <span className="text-xs text-slate-600 ml-auto">as of {fmtTime(asOf)}</span>
      </div>
      <div className="mt-2 text-xs text-slate-500 font-mono">
        {usdCompact(tree.cash)} cash · {usdCompact(tree.deployed)} deployed
      </div>
      {/* D2/F6: never show a smaller number as if it were the whole truth. */}
      {tree.partial && (
        <div className="badge bg-gold/15 text-gold mt-2">
          incomplete — {down.map((a) => a.label).join(", ")} unreachable
        </div>
      )}
    </div>
  );
}

function AccountCard({ a, paper }: { a: FirmAccount; paper: boolean }) {
  const ok = a.status === "ok";
  return (
    <div className={`card py-3 ${paper ? "border-dashed border-slate-700 bg-panel/40" : ""}`}>
      <div className="flex items-center gap-2">
        <span className={`w-2 h-2 rounded-full shrink-0 ${ok ? "bg-up" : "bg-gold animate-pulse"}`} />
        <span className="text-sm font-bold truncate">{a.label}</span>
        <span className="text-[10px] uppercase tracking-wider text-slate-600 ml-auto shrink-0">
          {a.kind} · {a.tax_wrapper}
        </span>
      </div>
      <div className={`font-mono text-xl font-bold mt-1 ${paper ? "text-slate-400" : ""}`}>
        {a.total == null ? "—" : usdCompact(a.total)}
      </div>
      {ok ? (
        <div className="text-xs text-slate-500 font-mono mt-0.5">
          {usd(a.cash ?? 0)} cash · {usd(a.deployed ?? 0)}{" "}
          {/* A margin venue reports MARGIN USED, not notional: $3.58 of margin
              can carry $180 of USD_CAD. Calling both "deployed" made the Firm
              page and Holdings look like they disagreed (Alan, 2026-08-19). */}
          {a.institution?.toLowerCase() === "oanda" ? (
            <span title="Margin posted against open positions — the position's notional is far larger. Holdings shows notional.">
              margin used
            </span>
          ) : "deployed"}
        </div>
      ) : (
        <div className="text-xs text-gold mt-0.5 break-words">unreachable — {a.error ?? "read failed"}</div>
      )}
    </div>
  );
}

function BookCard({ b, paper, open, onToggle }: {
  b: FirmBook; paper: boolean; open: boolean; onToggle: () => void;
}) {
  const tone = b.day_pnl == null ? "text-slate-500" : b.day_pnl >= 0 ? "text-up" : "text-down";
  return (
    <div className={`card py-3 ${paper ? "border-dashed border-slate-700 bg-panel/40" : ""}`}>
      <div className="flex items-baseline gap-2 flex-wrap">
        <button className="text-sm font-bold hover:text-accent text-left"
                onClick={() => openView("command", b.book)}>
          {b.label} <span className="text-slate-600 font-normal">→ Command</span>
        </button>
        <span className={`font-mono font-bold ml-auto ${paper ? "text-slate-400" : ""}`}>
          {usdCompact(b.equity)}
        </span>
      </div>
      <div className="mt-1 grid grid-cols-3 gap-2 text-xs font-mono">
        <Cell label="Day P&L" value={b.day_pnl == null ? "—" : signedUsd(b.day_pnl)} tone={tone} />
        <Cell label="Deployed" value={usdCompact(b.deployed)} />
        <Cell label="Headroom" value={usdCompact(b.headroom)} />
      </div>
      {/* A labelled control, not a hover affordance — the sleeve numbers must
          be reachable by thumb (Kaizen D3). */}
      <button className="mt-2 text-[11px] text-slate-500 hover:text-slate-300 underline"
              onClick={onToggle}>
        {open ? "hide sleeves" : `show ${b.sleeves.length} sleeve${b.sleeves.length === 1 ? "" : "s"}`}
      </button>
      {open && (
        <div className="mt-2 space-y-2">
          {b.sleeves.map((s) => {
            const used = s.budget > 0 ? Math.min(100, (s.deployed / s.budget) * 100) : 0;
            return (
              <div key={s.name}>
                <div className="flex items-baseline gap-2 text-xs">
                  <span className="font-semibold capitalize">{s.name}</span>
                  <span className="text-slate-500 font-mono">{s.position_count} open</span>
                  <span className={`font-mono ml-auto ${s.unrealized_pnl >= 0 ? "text-up" : "text-down"}`}>
                    {s.position_count > 0 ? signedUsd(s.unrealized_pnl) : "—"}
                  </span>
                </div>
                <div className="h-1.5 rounded bg-edge overflow-hidden mt-1">
                  <div className={`h-full ${paper ? "bg-slate-500" : "bg-accent"}`}
                       style={{ width: `${used}%` }} />
                </div>
                <div className="text-[11px] text-slate-500 font-mono mt-0.5">
                  {usd(s.deployed)} of {usd(s.budget)} · {usd(s.headroom)} free
                </div>
              </div>
            );
          })}
          {b.sleeves.length === 0 && <div className="text-xs text-slate-600">no sleeves reporting</div>}
        </div>
      )}
    </div>
  );
}

// Real owner capital that no book has claimed — the pro-rata splits' remainder
// (CFO §4.1c). It's inside the total above; without this row it reads as a
// rounding error between the accounts and the books.
function Buffers({ buffers }: { buffers: Record<string, number> }) {
  const rows = Object.entries(buffers).filter(([, v]) => v !== 0);
  if (rows.length === 0) return null;
  return (
    <div className="text-xs text-slate-500 font-mono">
      unallocated venue capital: {rows.map(([k, v]) => `${k.replace("_", " ")} ${usd(v)}`).join(" · ")}
    </div>
  );
}

function Cell({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-wider text-slate-600">{label}</div>
      <div className={`truncate ${tone ?? ""}`}>{value}</div>
    </div>
  );
}

function Segment({ on, onClick, tone, children }: {
  on: boolean; onClick: () => void; tone: MoneyType; children: React.ReactNode;
}) {
  const active = tone === "real"
    ? "border-accent text-accent bg-accent/10"
    : "border-slate-500 text-slate-300 bg-slate-500/10 border-dashed";
  return (
    <button onClick={onClick}
            className={`px-3 py-1 rounded-lg border text-xs font-bold transition-colors ${
              on ? active : "border-edge text-slate-500 hover:border-slate-500"}`}>
      {children}
    </button>
  );
}
